import { LogoStatus } from '../../../../../../__generated__/graphql/catalog/graphql'
import { LogoFile } from '../../../../../../containers/EmbroideryContainer/types'

const PENDING_STATUSES: readonly LogoStatus[] = [
  'PENDING',
  'SENT_TO_DIGITIZER',
  'DIGITIZATION_UNDER_REVIEW',
  'SENT_FOR_CUSTOMER_APPROVAL',
  'CUSTOMER_FEEDBACK',
]

export type LogoChoice = {
  logoFile: LogoFile
  /**
   * `new` is the logo this order pays to digitize. `pending` is still being digitized for an
   * earlier order, so it can't be chosen yet. `approved` is digitized and ready.
   */
  status: 'new' | 'pending' | 'approved'
}

/**
 * The logos a garment's logo form offers, new logo first.
 *
 * An order carries one new logo at most. While another garment carries one, it's the only new logo
 * offered, beside the account's digitized logos, and this garment's own upload is left out. Account
 * logos still being digitized are offered disabled; any other account logo isn't offered.
 *
 * @param newLogoInOrder - The new logo another garment in the order carries.
 * @param uploadedLogo - This garment's own upload, kept on offer while another logo is chosen.
 * @param chosenLogo - The logo the garment holds now, kept on offer if it's digitized.
 * @param userLogos - The logos saved to the customer's account.
 */
export function logoChoices({
  newLogoInOrder,
  uploadedLogo,
  chosenLogo,
  userLogos,
}: {
  newLogoInOrder: LogoFile | undefined
  uploadedLogo: LogoFile | undefined
  chosenLogo: LogoFile | undefined
  userLogos: readonly LogoFile[]
}): LogoChoice[] {
  const choices: LogoChoice[] = []
  const offer = (logoFile: LogoFile, status: LogoChoice['status']) => {
    if (!choices.some(choice => choice.logoFile.id === logoFile.id)) {
      choices.push({ logoFile, status })
    }
  }

  const newLogo = newLogoInOrder ?? uploadedLogo
  if (newLogo) offer(newLogo, 'new')

  for (const logoFile of userLogos) {
    if (logoFile.status === 'APPROVED') offer(logoFile, 'approved')
    else if (PENDING_STATUSES.includes(logoFile.status)) offer(logoFile, 'pending')
  }

  // Covers a digitized logo chosen before the account's logos have loaded.
  if (chosenLogo?.status === 'APPROVED') offer(chosenLogo, 'approved')

  return choices
}

/** The new logo, when it's the only logo on offer that can be chosen. */
export function onlyChoosableNewLogo(choices: readonly LogoChoice[]): LogoFile | undefined {
  const choosable = choices.filter(choice => choice.status !== 'pending')
  const [onlyChoice] = choosable

  return choosable.length === 1 && onlyChoice?.status === 'new' ? onlyChoice.logoFile : undefined
}

/**
 * The logo the picker shows as selected: the one the garment holds or awaits, else the only
 * choosable new logo. That default waits for the account's logos to load, and is dropped once a
 * placement is picked without it, so the customer adds it with one click instead.
 */
export function deriveSelectedLogo({
  chosenLogo,
  choices,
  isLoadingChoices,
  hasPlacement,
}: {
  chosenLogo: LogoFile | undefined
  choices: readonly LogoChoice[]
  isLoadingChoices: boolean
  hasPlacement: boolean
}): LogoFile | undefined {
  if (chosenLogo) return chosenLogo
  if (isLoadingChoices || hasPlacement) return undefined
  return onlyChoosableNewLogo(choices)
}

/**
 * Which step the logo editor shows. Adding a new logo opens the digitization step, which closes
 * once another garment in the order carries a new logo. Otherwise the editor shows the summary step
 * when there is a logo to choose, waiting for the account's logos to load to find out.
 */
export function deriveLogoEditorStep({
  isAddingNewLogo,
  canUploadNewLogo,
  hasChoices,
  isLoadingChoices,
}: {
  isAddingNewLogo: boolean
  canUploadNewLogo: boolean
  hasChoices: boolean
  isLoadingChoices: boolean
}): 'digitization' | 'summary' | undefined {
  if (isAddingNewLogo && canUploadNewLogo) return 'digitization'
  if (hasChoices) return 'summary'
  return isLoadingChoices ? undefined : 'digitization'
}
